import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { SeoHead } from "@/components/common/SeoHead";
import { WhatsAppCTA } from "@/components/common/WhatsAppCTA";
import { AnimatedSection } from "@/components/common/AnimatedSection";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Check, Ship, Anchor, Star, Globe, Users, Sun } from "lucide-react";

const BREADCRUMB = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://tripsyfy.com" },
    { "@type": "ListItem", position: 2, name: "Cruise Booking Assistance", item: "https://tripsyfy.com/cruise-booking-assistance" },
  ],
});

const FAQ_SCHEMA = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "How does Tripsyfy help with cruise booking?", acceptedAnswer: { "@type": "Answer", text: "Tripsyfy helps you choose the right cruise — cruise line, itinerary, cabin category, and timing — based on your preferences, budget, and group size. We then handle the full booking process including flights, transfers, and pre/post-cruise arrangements." } },
    { "@type": "Question", name: "Does Tripsyfy book all cruise lines?", acceptedAnswer: { "@type": "Answer", text: "We assist with bookings across major cruise lines including Royal Caribbean, Norwegian Cruise Line, Carnival, MSC Cruises, Princess Cruises, and Celebrity Cruises. For polar expeditions, we are an authorised partner of Quark Expeditions." } },
    { "@type": "Question", name: "What cruise itineraries does Tripsyfy cover?", acceptedAnswer: { "@type": "Answer", text: "We help with Caribbean cruises, Mediterranean cruises, Alaskan cruises, Hawaii, Northern Europe, South America, and exclusive polar expeditions to Antarctica and the Arctic." } },
    { "@type": "Question", name: "Is it better to book a cruise through Tripsyfy than direct?", acceptedAnswer: { "@type": "Answer", text: "Booking through Tripsyfy gives you personalized guidance on cruise line selection, cabin categories, and timing — as well as coordination of flights and pre/post-cruise hotels. We provide support that the cruise lines' own booking engines don't offer." } },
    { "@type": "Question", name: "Can Tripsyfy help first-time cruise passengers?", acceptedAnswer: { "@type": "Answer", text: "Yes. First-time cruisers are one of our most common client types. We explain how cruise pricing works, what's included vs. extra, which cruise lines suit different travel styles, and how to choose the right itinerary for a first cruise." } },
    { "@type": "Question", name: "How do I request cruise booking assistance from Tripsyfy?", acceptedAnswer: { "@type": "Answer", text: "Call us at +1 972-666-1122, message us on WhatsApp, or submit a quote request. Tell us your preferred cruise region, travel dates, group size, and budget — we'll present you with the best available options." } },
  ],
});

const cruiseTypes = [
  { icon: Sun, title: "Caribbean cruises", desc: "Island hopping, beaches, and warm weather. Perfect for families and first-time cruisers." },
  { icon: Globe, title: "Mediterranean cruises", desc: "Rome, Athens, Barcelona, and beyond — classic European culture by sea." },
  { icon: Ship, title: "Alaskan cruises", desc: "Glaciers, wildlife, and dramatic scenery. A bucket-list itinerary for nature lovers." },
  { icon: Anchor, title: "Northern Europe cruises", desc: "Norway fjords, Iceland, Scandinavia, and the British Isles." },
  { icon: Star, title: "Polar expeditions", desc: "Antarctica and Arctic small-ship expeditions. Expert naturalists, Zodiac landings, penguins." },
  { icon: Users, title: "Group and family cruises", desc: "Multi-cabin bookings for families and groups, coordinated with shore excursions." },
];

const whatWeHandle = [
  "Cruise line and itinerary selection based on your preferences",
  "Cabin category comparison (inside, oceanview, balcony, suite)",
  "Pricing comparison across sailing dates and ships",
  "Pre/post-cruise hotel coordination",
  "Flights to and from the departure port",
  "Transfer arrangements (airport to port)",
  "Shore excursion recommendations and bookings",
  "Dining preference setup and specialty restaurant reservations",
  "Travel insurance guidance for cruise travel",
  "Expedition cruise advisory (for Antarctica and Arctic trips)",
];

const faqs = [
  { q: "How does Tripsyfy help with cruise booking?", a: "We help you choose the right cruise — line, itinerary, cabin, and timing — then handle the full booking including flights, transfers, and pre/post-cruise arrangements." },
  { q: "Does Tripsyfy book all cruise lines?", a: "We assist with all major cruise lines including Royal Caribbean, Norwegian, Carnival, MSC, Princess, and Celebrity. For polar expeditions, we're an authorised Quark Expeditions partner." },
  { q: "What cruise itineraries does Tripsyfy cover?", a: "Caribbean, Mediterranean, Alaska, Hawaii, Northern Europe, South America, and polar expeditions to Antarctica and the Arctic." },
  { q: "Is it better to book a cruise through Tripsyfy than direct?", a: "Booking through us provides personalized guidance on cruise line selection, cabin categories, and timing — plus coordinated flights and pre/post hotels that cruise lines' own systems don't offer." },
  { q: "Can Tripsyfy help first-time cruise passengers?", a: "Yes. We explain how cruise pricing works, what's included vs. extra, which lines suit different styles, and how to pick the right itinerary for a first cruise." },
  { q: "How do I request cruise booking assistance from Tripsyfy?", a: "Call +1 972-666-1122, WhatsApp us, or submit a quote request. Tell us your preferred cruise region, travel dates, group size, and budget." },
];

export default function CruiseBookingAssistancePage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div id="main-content" className="min-h-screen bg-white">
      <Helmet>
        <script type="application/ld+json">{BREADCRUMB}</script>
        <script type="application/ld+json">{FAQ_SCHEMA}</script>
      </Helmet>
      <SeoHead
        title="Cruise Booking Assistance"
        description="Get help choosing and booking cruises with Tripsyfy. Caribbean, Mediterranean, Alaska, and polar expeditions — with expert guidance and full booking support."
        canonical="https://tripsyfy.com/cruise-booking-assistance"
      />
      <Navbar />

      <section className="relative pt-28 pb-14 bg-primary">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <p className="text-sky-300 text-xs font-semibold tracking-widest uppercase mb-3">Cruise Specialist</p>
          <h1 className="heading-xl text-white mb-3">Cruise Booking Assistance</h1>
          <p className="text-base text-white/70 max-w-xl leading-relaxed mb-8">
            Tripsyfy helps you choose the right cruise, navigate cabin pricing, and coordinate flights, transfers, and pre/post-cruise arrangements — so your entire voyage is seamless from start to finish.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/contact"><Button className="bg-accent text-white hover:bg-accent/90">Request a Cruise Quote</Button></Link>
            <WhatsAppCTA variant="button" />
          </div>
        </div>
      </section>

      <section className="section-padding bg-surface-subtle">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="max-w-2xl mb-10">
              <p className="overline mb-3">Cruise Types</p>
              <h2 className="heading-lg text-primary">What kind of cruise are you looking for?</h2>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {cruiseTypes.map(({ icon: Icon, title, desc }, i) => (
              <AnimatedSection key={title} delay={i * 0.06}>
                <div className="bg-white rounded-xl shadow-card p-5 flex gap-4">
                  <div className="h-10 w-10 rounded-xl bg-accent/10 flex items-center justify-center shrink-0">
                    <Icon className="h-5 w-5 text-accent" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-primary mb-1">{title}</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">{desc}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <AnimatedSection>
              <p className="overline mb-3">What We Handle</p>
              <h2 className="heading-lg text-primary mb-6">End-to-end cruise planning</h2>
              <ul className="space-y-3">
                {whatWeHandle.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="h-5 w-5 rounded-full bg-accent/10 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="h-3 w-3 text-accent" />
                    </div>
                    <span className="text-sm text-foreground leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </AnimatedSection>
            <AnimatedSection delay={0.1}>
              <div className="bg-surface-subtle rounded-xl p-7 mb-6">
                <p className="overline mb-3">Why Human Support Matters</p>
                <h3 className="heading-md text-primary mb-4">Cruise pricing is more complex than it looks</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                  Cruise prices vary by cabin category, sailing date, deck position, and included promotions. A small cabin difference can mean $800 per couple — in either direction. A knowledgeable advisor understands which cabin configurations are worth the premium and which promotions actually save money.
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                  For polar expeditions in particular, expedition companies price differently than mainstream cruise lines — and the difference between ships, departure dates, and cabin types is significant.
                </p>
                <WhatsAppCTA variant="banner" />
              </div>
              <div className="bg-primary rounded-xl p-6">
                <p className="text-sm font-semibold text-white mb-2">Polar Expedition Partner</p>
                <p className="text-xs text-white/70 leading-relaxed mb-4">
                  Tripsyfy is an authorised booking partner of Quark Expeditions — the world's polar expedition pioneer since 1991. We offer Antarctica and Arctic expedition bookings at the same price as booking direct.
                </p>
                <Link to="/expeditions/antarctica">
                  <Button variant="outline" className="w-full border-white/30 text-white hover:bg-white/10">
                    View Antarctica Expeditions
                  </Button>
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="max-w-2xl mb-10">
              <p className="overline mb-3">How It Works</p>
              <h2 className="heading-lg text-primary">How we help you find and book the right cruise</h2>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {[
              { num: "01", title: "Share your cruise preferences", desc: "Tell us your preferred region, travel dates, group size, and budget. First time cruising? We'll guide you through the options." },
              { num: "02", title: "We compare cruise options", desc: "Our team reviews cruise lines, available sailings, cabin categories, and included amenities across your dates and budget." },
              { num: "03", title: "You choose the cruise that fits", desc: "We present options with honest assessments of cabin differences, included meals, and what's worth the extra cost." },
              { num: "04", title: "We book and coordinate everything", desc: "We handle the full cruise booking plus your flights and port transfers — everything confirmed and documented in one plan." },
            ].map(({ num, title, desc }, i) => (
              <AnimatedSection key={num} delay={i * 0.08}>
                <div>
                  <p className="text-4xl font-bold text-primary/10 mb-2 leading-none">{num}</p>
                  <h3 className="text-sm font-bold text-primary mb-2">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-surface-subtle">
        <div className="container-wide px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto">
          <AnimatedSection>
            <p className="overline mb-3 text-center">Common Questions</p>
            <h2 className="heading-lg text-primary text-center mb-10">Cruise Booking Assistance</h2>
          </AnimatedSection>
          <div className="space-y-3">
            {faqs.map(({ q, a }, i) => (
              <AnimatedSection key={i} delay={i * 0.04}>
                <details className="bg-white rounded-xl shadow-card overflow-hidden">
                  <summary className="px-6 py-4 cursor-pointer font-semibold text-primary text-sm list-none flex items-center justify-between gap-4 hover:text-accent transition-colors">
                    {q}<span className="text-accent text-lg leading-none shrink-0">+</span>
                  </summary>
                  <div className="px-6 pb-5">
                    <p className="text-sm text-muted-foreground leading-relaxed">{a}</p>
                  </div>
                </details>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-primary">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-center">
            <div className="lg:col-span-2">
              <AnimatedSection>
                <h2 className="heading-lg text-white mb-3">Ready to book your cruise?</h2>
                <p className="text-white/70 text-base leading-relaxed">Tell us your preferred region, travel dates, and group size — we'll find the best available options.</p>
              </AnimatedSection>
            </div>
            <div className="flex flex-col gap-3 lg:items-end">
              <Link to="/contact"><Button className="bg-accent text-white hover:bg-accent/90 w-full lg:w-auto">Request a Cruise Quote</Button></Link>
              <WhatsAppCTA variant="button" />
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-white/10 flex flex-wrap gap-6 text-sm text-white/60">
            <Link to="/cruises" className="hover:text-white transition-colors">→ Browse All Cruises</Link>
            <Link to="/expeditions/antarctica" className="hover:text-white transition-colors">→ Antarctica Expeditions</Link>
            <Link to="/group-travel-planning" className="hover:text-white transition-colors">→ Group Travel Planning</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
